import { useCallback, useEffect, useReducer, useRef } from 'react';

import { markFilterViewed } from './markFilterViewed';
import { UserFilter } from './types';

type isNewFn = (filterId: string) => boolean | undefined;

/*
Optimistic filters view statuses storage. Holds filters 'viewed' statuses as they were passed initially.
Doesn't update status upon passing new filters array, only reacts to its functions (e.g. markAsViewed) calls.
markViewed / markAllAsViewed accept immediate parameter - only call the api vs call the api and change isNew status
 */
export const useFiltersViewedStatuses = (
  filters: UserFilter[]
): {
  markAllViewed: (immediate?: boolean) => void;
  markViewed: (filterId: string, immediate?: boolean) => void;
  isNew: isNewFn;
} => {
  const [, updateIsNew] = useReducer(() => ({}), {});
  const statusesRef = useRef(new Map<string, { viewed: boolean; isSent: boolean }>());
  const statuses = statusesRef.current;

  useEffect(() => {
    const statusesAreEqual = statuses.size === filters.length && filters.every(x => statuses.has(x.id));
    if (statusesAreEqual) {
      return;
    }

    for (const filter of filters) {
      if (!statuses.has(filter.id)) {
        statuses.set(filter.id, { viewed: filter.viewed, isSent: false });
      }
    }

    [...statuses.keys()].filter(id => !filters.find(f => f.id === id)).forEach(id => statuses.delete(id));
  }, [filters, statuses]);

  const isNew = useCallback(
    (filterId: string) => {
      const state = statuses.get(filterId);
      if (state === undefined) {
        return undefined;
      }

      return !state.viewed;
    },
    [statuses]
  );

  const markViewed = useCallback(
    (filterId: string, immediate: boolean = false) => {
      const filterState = statuses.get(filterId);
      if (filterState === undefined || filterState.viewed) {
        return;
      }

      if (!filterState.isSent) {
        markFilterViewed(filterId).catch();
        filterState.isSent = true;
      }

      filterState.viewed = immediate;
      updateIsNew();
    },
    [statuses]
  );

  const markAllViewed = useCallback(
    (immediate: boolean = false) => {
      [...statuses.keys()].forEach(x => markViewed(x, immediate));
    },
    [markViewed, statuses]
  );

  return {
    markAllViewed,
    markViewed,
    isNew,
  };
};
